/**
 * React Hook for Automatic Page View Tracking
 * Tracks page views when route changes in React applications
 */

import { useEffect, useRef } from 'react';
import { analytics } from '../lib/analytics-events';

/**
 * Hook to track page views automatically
 * Call this in your main App component or router setup
 */
export function usePageTracking() {
  const previousPath = useRef<string>('');

  useEffect(() => {
    const trackPageView = () => {
      const currentPath = window.location.pathname + window.location.search;

      // Only track if path changed
      if (currentPath !== previousPath.current) {
        previousPath.current = currentPath;

        analytics.trackPageView({
          page_title: document.title,
          page_location: window.location.href,
          page_path: currentPath,
        });
      }
    };

    // Track initial page view
    trackPageView();

    // Track page views on history changes (for SPA navigation)
    const handleHistoryChange = () => {
      trackPageView();
    };

    // Listen for both pushState and replaceState
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleHistoryChange();
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleHistoryChange();
    };

    // Listen for popstate (browser back/forward buttons)
    window.addEventListener('popstate', handleHistoryChange);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handleHistoryChange);
    };
  }, []);
}
